import type { PresetCategory } from "@pax/api-contract/presets/v1alpha1";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { SearchWall, SearchWallSkeleton } from "@/features/presets/components/SearchWall";
import {
  type CatalogSort,
  categoriesToParameter,
  DEFAULT_SORT,
  filterByCategory,
  normalizeCatalogSort,
  normalizeCategories,
  sortCatalog,
} from "@/features/presets/ordering";
import { normalizePresetSearch, presetListingsQuery } from "@/features/presets/queries";
import { repeatedCatalog } from "@/features/presets/sample-catalog";

const SEARCH_DEBOUNCE_MS = 300;

interface WallSearch {
  q?: string;
  sort?: CatalogSort;
  category?: string;
}

export const Route = createFileRoute("/search")({
  validateSearch: (search: Record<string, unknown>): WallSearch => {
    const q = normalizePresetSearch(search.q);
    const sort = normalizeCatalogSort(search.sort);
    const category = categoriesToParameter(normalizeCategories(search.category));
    return { ...(q && { q }), ...(sort && { sort }), ...(category && { category }) };
  },
  // Only the query reaches the catalog. Order and category arrange what has
  // arrived, so changing either is not a new request.
  loaderDeps: ({ search: { q } }) => ({ q }),
  loader: ({ context: { queryClient }, deps: { q } }) =>
    queryClient.ensureInfiniteQueryData(presetListingsQuery(q)),
  head: () => ({ meta: [{ title: "Search scenarios · Pax.co" }] }),
  pendingComponent: SearchWallSkeleton,
  component: SearchPage,
});

function SearchPage() {
  const { q, sort = DEFAULT_SORT, category } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [searchValue, setSearchValue] = useState(q ?? "");
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError } =
    useSuspenseInfiniteQuery(presetListingsQuery(q));

  useEffect(() => {
    setSearchValue(q ?? "");
  }, [q]);

  // The field is typed into far faster than the catalog can answer, so the
  // URL — which is what asks — follows a few hundred milliseconds behind it.
  useEffect(() => {
    const nextQuery = normalizePresetSearch(searchValue);
    if (nextQuery === q) return;

    const timer = window.setTimeout(() => {
      void navigate({
        search: previous => ({ ...previous, q: nextQuery }),
        replace: true,
        resetScroll: false,
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [navigate, q, searchValue]);

  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);
  const narrow = useCallback(
    (next: Partial<WallSearch>) =>
      void navigate({
        search: previous => ({ ...previous, ...next }),
        replace: true,
        resetScroll: false,
      }),
    [navigate],
  );

  const categories = normalizeCategories(category);
  const found = sortCatalog(
    filterByCategory(
      data.pages.flatMap(page => page.items),
      categories,
    ),
    sort,
  );

  return (
    <SearchWall
      listings={repeatedCatalog(found)}
      query={q ?? ""}
      searchValue={searchValue}
      sort={sort}
      categories={categories}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      loadMoreFailed={isFetchNextPageError}
      onSearchChange={setSearchValue}
      onSortChange={(next: CatalogSort) => narrow({ sort: next })}
      onCategoriesChange={(next: PresetCategory[]) =>
        narrow({ category: categoriesToParameter(next) })
      }
      onLoadMore={loadMore}
    />
  );
}
